import { Backdrop, Box, Card, CardContent, Divider, Grid, IconButton, InputAdornment, Link, List, ListItem, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check';
import { ChangeEventHandler, Dispatch, FC, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StorageFirewallIpInput } from "@/gen/api/user";
import { CRUD_DELETE, ListContextProvider, useDelete, useListContext, useListController, useSaveContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import Create from "@/controller/Create";
import { SetStateAction } from "react";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { CLOUD_MANAGEMENT } from "@/constants";


const resource = 'settings/organization/ip-whitelist'

const IpWhitelist: FC = () => {
  const listCtrl = useListController({ resource, basePath: '' })
  return (
    <ListContextProvider value={listCtrl}>
      <IpWhitelistBody />
    </ListContextProvider>
  )
}

const initState = {
  openAdd: false,
  ipRecToAdd: '',
  invalidIpRecToAdd: false,
  ipRecToDel: '',
  ipChange: null as {ip:string, isDelete:boolean, syncCount:number}|null,
}
type InnerState = typeof initState

const IpWhitelistBody: FC = () => {
  const hasWritePermit = useHasPermit([CLOUD_MANAGEMENT])
  const [states, setStates] = useState(initState)
  const inputRef = useRef<HTMLInputElement>()

  const { data, ids, refetch, loading } = useListContext<StorageFirewallIpInput&RecPatch>()

  const delHandlers = useRequestRecievedHandler("Deleting IP record")
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch()
    setStates(v => ({...v, ipRecToDel: '', ipChange: { ip:v.ipRecToDel, isDelete:true, syncCount:3 }}))
  }, [refetch, setStates]))
  const [deleteOne, { loading: deleting }] = useDelete(resource, undefined, null, { 
    action: CRUD_DELETE,
    mutationMode: 'pessimistic',
    ...delHandlers,
  })
  
  const createHandlers = useRequestRecievedHandler("Creating IP record")
  createHandlers.onSuccess.merge(refetch)

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    if (!loading && ids) {
      setStates(v => {
        if (!v.ipChange) { return v }

        if (ids.indexOf(v.ipChange.ip) < 0 === v.ipChange.isDelete) {
          return {...v, ipChange: null }
        } else if (v.ipChange.syncCount > 0) {
          setTimeout(refetch)
          return {...v, ipChange: {...v.ipChange, syncCount: v.ipChange.syncCount - 1 }}
        } else {
          setTimeout(() => {
            enqueueSnackbar(`IP Record Change has failed in background`, { variant: 'warning' })
          })
          return {...v, ipChange: null }
        }
      })
    }
  }, [loading, ids, enqueueSnackbar])
  return (
    <Card>
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <List disablePadding 
              subheader={
                states.openAdd
                  ? <Create resource={resource} basePath="" {...createHandlers}>
                      {hasWritePermit && <IPRecordCreationInput states={states} setStates={setStates} ref={inputRef} />}
                    </Create>
                  : <Stack direction="row" alignItems="center" spacing={2} component="li">
                      <b>IP Address or CIDR</b>
                      {hasWritePermit &&
                        <Tooltip title="Add">
                          <IconButton 
                            color="success"
                            onClick={() => {
                              setStates(v => ({ ...v, openAdd: true }))
                              setTimeout(() => { inputRef.current?.focus() }, 50)
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>}
                      <WhitelistHelpTip />
                    </Stack>
              }
              sx={{
                '& li.MuiListItem-root': {
                  '&:hover': {
                    bgcolor: theme => theme.palette.action.hover,
                    '& button.MuiIconButton-root': { opacity: 1 },
                  },
                  '& button.MuiIconButton-root': { 
                    opacity: 0,
                    '&:focus': { opacity: 1 },
                  },
                }
              }}
            >
              <Divider sx={{ mt: 1 }} component="li" />
              {ids.map(id => (
                <ListItem divider key={id} 
                  secondaryAction={
                    hasWritePermit &&
                    <Tooltip title="Delete">
                      <IconButton color="primary" disabled={deleting || !!states.ipChange}
                        onClick={() => {
                          setStates(v => ({...v, ipRecToDel: id as any }))
                          deleteOne(resource, id, data[id])
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  {id}
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default IpWhitelist;

const IPRecordCreationInput = forwardRef<HTMLInputElement|undefined, {
  setStates:Dispatch<SetStateAction<InnerState>>, 
  states:InnerState,
}>(({
  setStates,
  states,
}, ref) => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const clearClose = (traceSync?:boolean) => setStates(v => ({ ...v, 
    openAdd: false, 
    ipRecToAdd: '', 
    invalidIpRecToAdd: false, 
    ipChange: traceSync ? { ip: v.ipRecToAdd, isDelete: false, syncCount: 3 } : null 
  }))
  
  const rgx = /^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}$/
  const validateIp = (s:string) => {
    const parts = s.split('/')
    if (parts.length > 2) { return false }
    const [ip, mask] = parts
    if (!rgx.test(ip) || mask === '') { return false }
    let maskNum = 0
    if (mask) {
      maskNum = Number(mask)
      if (isNaN(maskNum) || maskNum < 1 || maskNum > 32) {
        return false
      }
    }
    return true 
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setStates(v => ({
      ...v,
      ipRecToAdd: e.target.value,
      invalidIpRecToAdd: !validateIp(e.target.value)
    }))
  }
  const handleSave = () => {
    if (!states.ipRecToAdd || saving || states.invalidIpRecToAdd) { return }
    save({ ip: states.ipRecToAdd, isCidr: states.ipRecToAdd.indexOf('/') > -1 }, {
      onSuccess: () => {
        clearClose(true)
      },
    })
  }

  return (
    <Box>
      <TextField
        inputRef={ref}
        id="create-ip-rec"
        size="small"
        label="IP Address or CIDR"
        value={states.ipRecToAdd}
        error={states.invalidIpRecToAdd}
        helperText={states.invalidIpRecToAdd ? 'invalid format' : ''}
        InputProps={{
          endAdornment:
            <InputAdornment position="end">
              <IconButton
                color="success"
                aria-label="create ip whitelist record"
                disabled={!states.ipRecToAdd || saving || !!states.ipChange || states.invalidIpRecToAdd}
                onClick={handleSave}
                edge="end"
              >
                <CheckIcon />
              </IconButton>
            </InputAdornment>
        }}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            handleSave()
            e.preventDefault()
          }
        }}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      />
      <Backdrop open={states.openAdd} onClick={() => clearClose()} invisible={true} />
    </Box>
  )
})

const WhitelistHelpTip = () => {
  const [anchorEl, setAnchorEl] = useState(null as HTMLButtonElement|null)
  const handleOpen = (e:React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)
  const open = !!anchorEl
  return (
    <>
      <Box sx={{ flex: '1 1 0'}} />
      <IconButton sx={{ ml: 'auto' }} onClick={handleOpen} title="whitelist-help-button" color="info" size="small">
        <InfoIcon />
      </IconButton>
      <Popover
        id="whitelist-tip-pop"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { maxWidth: 500 }
        }}
      >
        <Typography sx={{ p: 2 }} variant="body2">
          This "IP Whitelist" is the place for configuring who would have access to the cloud storage. 
          When using our OnData Scripts to upload/download database backup files, you should first list your Ip in here. 
        </Typography>
      </Popover>
    </>
  )
}


const useRequestRecievedHandler = (msg:string) => {
  const { enqueueSnackbar } = useSnackbar()
  return useMemo(() => {
    const successHandlers = [
      function defaultOnSuccess({data}: {data:RecPatch}) {
        const labelStr = msg
        enqueueSnackbar(`${labelStr} submitted`, { variant: 'success' })
      }
    ]
    const failureHandlers = [
      function defaultOnFailure(err:any) {
        const errMsg = retrieveAxiosErr(err)
        const labelStr = msg
        enqueueSnackbar(`${labelStr} failed: ${errMsg}`, { variant: 'error' })
      }
    ]
    const onSuccess = (x: {data:RecPatch}) => {
      for(const handler of successHandlers) { handler(x) }
    }
    const onFailure = (err: any) => {
      for(const handler of failureHandlers) { handler(err) }
    }
    onSuccess.merge = (handler: (x:{data:RecPatch}) => void) => {
      if (successHandlers.indexOf(handler) === -1) {
        successHandlers.push(handler)
      }
      return onSuccess
    }
    onFailure.merge = (handler: (err:any) => void) => {
      if (failureHandlers.indexOf(handler) === -1) {
        failureHandlers.push(handler)
      }
      return onFailure
    }
    return {
      onSuccess,
      onFailure,
    }
  }, [enqueueSnackbar])
}
